import debug from 'debug';
import router from '@/router';
import globals from '@/globals';

import logger from '../../lib/logger';
import isMobileHelper from '../../lib/isMobile';

import onboardingService from './onboardingV2.service';

const log = debug('store:onboardingV2');

async function handleUnexpectedError(error) {
  logger.error(error);
  await router.replace({ name: 'payment-link-error' }).catch((err) => log('router error', err));
  throw error;
}

function hiddenPhone({ phone }) {
  if (!phone) return phone;
  const phoneDDD = phone.substring(0, 2);
  const substring = phone.substring(7);
  return `(${phoneDDD}) *****-${substring}`;
}

export default {
  namespaced: true,
  state: {
    nextRoute: null,
    routeParams: null,
    isMobile: isMobileHelper(),
    transaction: null,
    onboarding: null,
    hiddenPhone: null,
    selfieFlow: null,
    showLandscapeWarning: null,
    allowToOpenCamera: false,
    totalValue: null,
    type: null,
    confirmationResendType: null,
    showResendOptions: false,
    chosenConfirmationResendType: false,
    disableTheResendCounter: false,
    insuranceOptions: null,
    birthdate: null,
  },
  actions: {
    async getOnboarding({ commit, dispatch, state }, { transactionId }) {
      try {
        const { data } = await onboardingService.getOnboarding({
          transactionId,
          isMobile: state.isMobile,
        });
        const {
          onboarding,
          transaction,
          checkout,
        } = data;
        if (['OPEN', 'PENDING', 'CANCELED'].includes(transaction.status)) {
          commit('setOnboardingData', {
            onboarding,
            transaction,
            checkout,
          });
        }

        dispatch('setNextRoute');
      } catch (error) {
        if (error && error.response && [401, 500].includes(error.response.status)) {
          commit('setIsOnboardingTokenValid', false);
          dispatch('auth/setToken', { token: null }, { root: true });
          await router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
      }
    },
    async sendConfirmationCode({ commit, dispatch, state }, { securityCode }) {
      const { onboarding } = state;

      try {
        const { data } = await onboardingService.sendConfirmation({
          onboardingId: onboarding._id,
          securityCode,
        });

        const updatedOnboarding = data.onboarding;
        commit('setOnboardingData', { onboarding: updatedOnboarding });

        dispatch('setNextRoute');
        return { success: true };
      } catch (error) {
        if ([
          'Security code not found',
        ].includes(error.response.data)) {
          return { success: false };
        }
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          await router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
        return { success: false };
      }
    },
    async resendConfirmation({ dispatch, state }) {
      const { onboarding, isMobile } = state;

      try {
        await onboardingService.resendConfirmation({
          onboardingId: onboarding._id,
          isMobile,
          type: state.confirmationResendType,
        });
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          await router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else if (error.response.data !== 'Last security code not sent a long ago') {
          await handleUnexpectedError(error);
        }
      }
    },
    async sendImage({ commit, dispatch, state }, { base64Img, extraData }) {
      const { onboarding } = state;
      try {
        const { data } = await onboardingService.sendImage({
          onboardingId: onboarding._id,
          step: onboarding.step,
          base64Img,
          extraData,
        });
        const { photoOk } = data;
        if (!photoOk) return { success: false, message: 'Foto inválida' };
        commit('setOnboardingData', { onboarding: data.onboarding });
        dispatch('setNextRoute');
        return { success: true, message: 'Foto enviada' };
      } catch (error) {
        if (error && error.response) {
          if (error.response.status === 413) {
            return { success: false, message: 'Imagem muito pesada' };
          }
          if (
            error.response.status === 500
            && error.response.data
            && error.response.data.startsWith('child "base64Img" fails')
          ) {
            return { success: false, message: 'Arquivo inválido!' };
          }
          return { success: false, message: error.response.data };
        }
        return { success: false, message: 'Unhandled Error' };
      }
    },
    async getOnboardingLink({ dispatch, state }) {
      const { onboarding } = state;

      try {
        await onboardingService.getOnboardingLink({
          onboardingId: onboarding._id,
        });
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          await router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else if (error.response.data !== 'Last security code not sent a long ago') {
          await handleUnexpectedError(error);
        }
      }
    },
    startListeningLandscape({ commit }) {
      if (window.orientation !== 0) this.landscape = true;
      window.addEventListener('orientationchange', () => {
        if (window.orientation === 0) {
          return commit('setShowLandscapeWarning', { landscape: false });
        }
        return commit('setShowLandscapeWarning', { landscape: true });
      }, false);
    },
    setNextRoute({ state, commit }) {
      const { onboarding, isMobile } = state;

      if (!isMobile && ['FRONTDOC', 'BACKDOC', 'SELFIE'].includes(onboarding.step)) {
        return commit('setNextRoute', { nextRoute: 'v2-onboarding-redirect-to-mobile' });
      }

      switch (onboarding.step) {
        case 'CONFIRMATION':
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-confirmation' });
        case 'FRONTDOC':
          return commit('setNextRoute', {
            nextRoute: 'v2-onboarding-documents',
            routeParams: {
              documentStep: 'frontdoc',
            },
          });
        case 'BACKDOC':
          return commit('setNextRoute', {
            nextRoute: 'v2-onboarding-documents',
            routeParams: {
              documentStep: 'backdoc',
            },
          });
        case 'SELFIE':
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-selfie' });
        case 'DESK':
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-desk' });
        case 'ENTRY':
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-entry' });
        case 'INSURANCE':
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-insurance' });
        case 'SIGNATURE':
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-signature-contract-view' });
        case 'COMPLETE':
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-complete' });
        default:
          return commit('setNextRoute', { nextRoute: 'v2-onboarding-insurance' });
      }
    },
    async fetchInsuranceOptions({ commit, state }) {
      const { onboarding } = state;
      try {
        const { data } = await onboardingService
          .getInsuranceOptions(onboarding._id);
        commit('setInsuranceOptions', data[0]);
      } catch (error) {
        throw new Error(error);
      }
    },
    async sendInsuranceConfirmation({ state }, hasInsurance) {
      const { onboarding } = state;
      try {
        await onboardingService
          .sendInsuranceConfirmation(onboarding._id, hasInsurance);
      } catch (error) {
        throw new Error(error);
      }
    },
    setLoading({ commit }, { loading }) {
      commit('setLoading', loading);
    },
    setAllowToOpenCamera({ commit }, { allow }) {
      commit('setAllowToOpenCamera', { allow });
    },
    setShowResendOptions({ commit }, { showResendOptions }) {
      commit('setShowResendOptions', { showResendOptions });
    },
    async acceptContract({ state }) {
      await onboardingService.sendContractConfirmation({
        onboardingId: state.onboarding._id,
      });
    },
  },
  mutations: {
    setNextRoute(state, { nextRoute, routeParams }) {
      state.nextRoute = nextRoute;
      state.routeParams = routeParams;
    },
    setOnboardingData(state, { onboarding, transaction, checkout }) {
      if (onboarding) {
        state.onboarding = onboarding;
        state.hiddenPhone = hiddenPhone({ phone: onboarding.phone });
        state.selfieFlow = onboarding.selfieFlow || 'FACETEC';
      }
      if (transaction) state.transaction = transaction;
      if (checkout) {
        state.totalValue = checkout && checkout.totalValue;
        state.birthdate = checkout && checkout.birthdate;
      }
    },
    setLoading(state, loading) {
      if (loading === false);
      setTimeout(() => { state.loading = loading; }, 1000);
    },
    setShowLandscapeWarning(state, { landscape }) {
      state.showLandscapeWarning = state.isMobile && landscape;
    },
    setAllowToOpenCamera(state, { allow }) {
      state.allowToOpenCamera = allow;
    },
    setResendConfirmationType(state, newConfirmationResendType) {
      state.confirmationResendType = newConfirmationResendType;
    },
    setChosenConfirmationResendType(state) {
      state.chosenConfirmationResendType = !state.chosenConfirmationResendType;
    },
    setShowResendOptions(state, { showResendOptions }) {
      state.showResendOptions = showResendOptions;
    },
    setDisableTheResendCounter(state) {
      state.disableTheResendCounter = !state.disableTheResendCounter;
    },
    setInsuranceOptions(state, data) {
      state.insuranceOptions = data;
    },
  },
  getters: {
    onboardingId(state) {
      return state.onboarding._id.toString();
    },
    getBirthdate(state) {
      return state.birthdate;
    },
    totalValue(state) {
      return state.totalValue;
    },
    ccbUrl(state) {
      const { _id } = state.transaction;
      const { apiUrl } = globals();

      return `${apiUrl}/public/ccb/transaction/${_id.toString()}`;
    },
    loading(state) {
      return state.loading;
    },
    onboarding(state) {
      return state.onboarding;
    },
    transaction(state) {
      return state.transaction;
    },
    insuranceOptions(state) {
      return state.insuranceOptions;
    },
  },
};
